import React, { useState, useEffect } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, ArrowLeft, Plus, Trash2, CheckCircle2 } from 'lucide-react';
import {
  ALERT_CHANNELS,
  BATTERY_CHEMISTRIES,
  CHINA_RAW_OPTIONS,
  COMPANY_TYPES,
  FOCUS_MATERIALS,
  MOTOR_TYPES,
  WEEKDAYS,
  createSupplierRows,
  initialCorporateFormState,
  loadCorporateProfile,
  saveCorporateProfile,
} from '../utils/corporateProfileStorage';

const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#03396c] focus:border-transparent text-sm';

const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

function SectionHeader({ letter, title, subtitle }) {
  return (
    <div className="flex items-start gap-3 pt-2 pb-4 border-b border-gray-200 dark:border-gray-700 mb-6">
      <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#03396c] dark:bg-blue-800 text-white text-sm font-bold flex items-center justify-center">
        {letter}
      </span>
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
        {subtitle && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children, className = '' }) {
  return (
    <div className={className}>
      <label className={labelClass}>
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const CorporatePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const forceSetup = searchParams.get('setup') === '1';

  const [view, setView] = useState('loading');
  const [form, setForm] = useState(initialCorporateFormState);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const saved = loadCorporateProfile();
    if (saved) {
      setForm(saved);
      setView(forceSetup ? 'form' : 'dashboard');
    } else {
      setView('form');
    }
  }, [forceSetup]);

  if (view === 'loading') {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center text-gray-500">
        Loading…
      </div>
    );
  }

  if (view === 'dashboard') {
    return <Navigate to="/corporate/alerts" replace />;
  }

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setSubmitted(false);
  };

  const toggleChemistry = (chemistry) => {
    setForm((prev) => {
      const set = new Set(prev.batteryChemistries);
      if (set.has(chemistry)) set.delete(chemistry);
      else set.add(chemistry);
      return { ...prev, batteryChemistries: [...set] };
    });
    setSubmitted(false);
  };

  const updateSupplier = (index, field, value) => {
    setForm((prev) => {
      const suppliers = [...prev.suppliers];
      suppliers[index] = { ...suppliers[index], [field]: value };
      return { ...prev, suppliers };
    });
    setSubmitted(false);
  };

  const updateUser = (index, field, value) => {
    setForm((prev) => {
      const dashboardUsers = [...prev.dashboardUsers];
      dashboardUsers[index] = { ...dashboardUsers[index], [field]: value };
      return { ...prev, dashboardUsers };
    });
    setSubmitted(false);
  };

  const addUser = () => {
    setForm((prev) => ({
      ...prev,
      dashboardUsers: [
        ...prev.dashboardUsers,
        { name: '', designation: '', alertChannel: 'Email' },
      ],
    }));
  };

  const removeUser = (index) => {
    setForm((prev) => ({
      ...prev,
      dashboardUsers: prev.dashboardUsers.filter((_, i) => i !== index),
    }));
  };

  const updateCompetitor = (index, value) => {
    setForm((prev) => {
      const competitors = [...prev.competitors];
      competitors[index] = value;
      return { ...prev, competitors };
    });
    setSubmitted(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!form.companyLegalName.trim()) {
      setError('Company legal name is required.');
      return;
    }
    if (!form.companyMakes) {
      setError('Please select what your company makes.');
      return;
    }
    saveCorporateProfile(form);
    setSubmitted(true);
    navigate('/corporate/alerts', { replace: true });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200">
      <div className="container mx-auto px-4 sm:px-6 py-8 md:py-12 max-w-3xl">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-[#03396c] dark:text-blue-400 hover:underline mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>

        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 rounded-xl bg-[#03396c]/10 dark:bg-blue-900/30">
            <Building2 className="h-8 w-8 text-[#03396c] dark:text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Corporate Profile</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm md:text-base">
              Tell us about your operations across lithium, cobalt, nickel, graphite, and
              rare earths so Stratosphere can map supply-chain exposure from India&apos;s
              perspective.
            </p>
          </div>
        </div>

        {submitted && (
          <div className="mb-6 flex items-start gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200">
            <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm">Profile saved. Opening Command Centre…</p>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-10">
          {/* A. WHO YOU ARE */}
          <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-6 md:p-8">
            <SectionHeader
              letter="A"
              title="Who You Are"
              subtitle="Company identity and manufacturing footprint in India"
            />
            <div className="space-y-5">
              <Field label="Company legal name" required>
                <input
                  type="text"
                  value={form.companyLegalName}
                  onChange={(e) => update('companyLegalName', e.target.value)}
                  className={inputClass}
                  placeholder="e.g. ABC Mobility Pvt Ltd"
                />
              </Field>
              <Field label="What does the company make?" required>
                <select
                  value={form.companyMakes}
                  onChange={(e) => update('companyMakes', e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select type</option>
                  {COMPANY_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Battery chemistry">
                <div className="flex flex-wrap gap-3">
                  {BATTERY_CHEMISTRIES.map((c) => (
                    <label
                      key={c}
                      className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={form.batteryChemistries.includes(c)}
                        onChange={() => toggleChemistry(c)}
                        className="rounded border-gray-300 text-[#03396c] focus:ring-[#03396c]"
                      />
                      {c}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Factory state(s) in India">
                <input
                  type="text"
                  value={form.factoryStates}
                  onChange={(e) => update('factoryStates', e.target.value)}
                  className={inputClass}
                  placeholder="e.g. Tamil Nadu, Maharashtra"
                />
              </Field>
            </div>
          </section>

          {/* B. WHAT YOU MAKE */}
          <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-6 md:p-8">
            <SectionHeader letter="B" title="What You Make" subtitle="Production volumes and product specs" />
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Annual units produced — last financial year">
                <input
                  type="number"
                  min="0"
                  value={form.annualUnitsLastFY}
                  onChange={(e) => update('annualUnitsLastFY', e.target.value)}
                  className={inputClass}
                  placeholder="Units"
                />
              </Field>
              <Field label="Planned production — next quarter (units)">
                <input
                  type="number"
                  min="0"
                  value={form.plannedProductionNextQuarter}
                  onChange={(e) => update('plannedProductionNextQuarter', e.target.value)}
                  className={inputClass}
                  placeholder="Units"
                />
              </Field>
              <Field label="Average battery pack size across models (kWh)">
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={form.avgBatteryPackKwh}
                  onChange={(e) => update('avgBatteryPackKwh', e.target.value)}
                  className={inputClass}
                  placeholder="kWh"
                />
              </Field>
              <Field label="Motor type">
                <select
                  value={form.motorType}
                  onChange={(e) => update('motorType', e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select motor type</option>
                  {MOTOR_TYPES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </section>

          {/* C. WHAT YOU SPEND */}
          <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-6 md:p-8">
            <SectionHeader letter="C" title="What You Spend" subtitle="Material cost and currency exposure" />
            <div className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Total annual material cost (₹ Crore) — last FY">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.totalAnnualMaterialCostCr}
                    onChange={(e) => update('totalAnnualMaterialCostCr', e.target.value)}
                    className={inputClass}
                    placeholder="₹ Crore"
                  />
                </Field>
                <Field label="Quarterly material cost budget (₹ Crore) — current quarter">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.quarterlyMaterialBudgetCr}
                    onChange={(e) => update('quarterlyMaterialBudgetCr', e.target.value)}
                    className={inputClass}
                    placeholder="₹ Crore"
                  />
                </Field>
              </div>
              <Field label="FX budget rate assumed by finance team (₹ per USD)">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.fxBudgetRateUsd}
                  onChange={(e) => update('fxBudgetRateUsd', e.target.value)}
                  className={inputClass}
                  placeholder="e.g. 83.50"
                />
              </Field>
              <Field label="Approximate annual spend per material (₹ Crore) — ballpark is fine">
                <textarea
                  value={form.approximateSpendPerMaterial}
                  onChange={(e) => update('approximateSpendPerMaterial', e.target.value)}
                  rows={4}
                  className={inputClass}
                  placeholder="e.g. Lithium: 120 Cr, Graphite: 45 Cr, Cobalt: 30 Cr…"
                />
              </Field>
              <Field label="Which materials are purchased in INR vs USD?">
                <textarea
                  value={form.materialsInrVsUsd}
                  onChange={(e) => update('materialsInrVsUsd', e.target.value)}
                  rows={3}
                  className={inputClass}
                  placeholder="e.g. Graphite: USD, Rare earths: USD, Nickel compounds: INR…"
                />
              </Field>
            </div>
          </section>

          {/* D. WHERE YOU BUY FROM */}
          <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-6 md:p-8">
            <SectionHeader
              letter="D"
              title="Where You Buy From"
              subtitle="Supplier geography, lead times, and inventory cover"
            />
            <div className="space-y-6">
              <div>
                <p className={labelClass}>
                  Supplier source country per material + volume share %
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  e.g. Graphite: China 86%, India 14%
                </p>
                <div className="space-y-4">
                  {form.suppliers.map((row, idx) => (
                    <div
                      key={row.material}
                      className="p-4 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800"
                    >
                      <p className="text-sm font-semibold text-[#03396c] dark:text-blue-400 mb-3">
                        {row.material}
                      </p>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Source country & share">
                          <input
                            type="text"
                            value={row.sourceCountry}
                            onChange={(e) =>
                              updateSupplier(idx, 'sourceCountry', e.target.value)
                            }
                            className={inputClass}
                            placeholder="China 86%, India 14%"
                          />
                        </Field>
                        <Field label="Volume share % (optional detail)">
                          <input
                            type="text"
                            value={row.volumeSharePercent}
                            onChange={(e) =>
                              updateSupplier(idx, 'volumeSharePercent', e.target.value)
                            }
                            className={inputClass}
                            placeholder="e.g. 86"
                          />
                        </Field>
                        <Field label="Domestic supplier's raw material from China?" className="sm:col-span-2">
                          <select
                            value={row.domesticRawFromChina}
                            onChange={(e) =>
                              updateSupplier(idx, 'domesticRawFromChina', e.target.value)
                            }
                            className={inputClass}
                          >
                            <option value="">Select</option>
                            {CHINA_RAW_OPTIONS.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                        </Field>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Typical lead time from main supplier (days)">
                  <input
                    type="number"
                    min="0"
                    value={form.leadTimeDays}
                    onChange={(e) => update('leadTimeDays', e.target.value)}
                    className={inputClass}
                    placeholder="Days"
                  />
                </Field>
                <Field label="Long-term contract vs spot (% per material)">
                  <input
                    type="text"
                    value={form.contractVsSpotPercent}
                    onChange={(e) => update('contractVsSpotPercent', e.target.value)}
                    className={inputClass}
                    placeholder="e.g. Lithium 80% LT / 20% spot"
                  />
                </Field>
              </div>
              <Field label="Current safety stock per material (days of production cover)">
                <textarea
                  value={form.safetyStockPerMaterial}
                  onChange={(e) => update('safetyStockPerMaterial', e.target.value)}
                  rows={3}
                  className={inputClass}
                  placeholder="e.g. Graphite: 21 days, Cobalt: 14 days…"
                />
              </Field>
            </div>
          </section>

          {/* E. WHO TO WATCH & WHO TO ALERT */}
          <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-6 md:p-8">
            <SectionHeader
              letter="E"
              title="Who to Watch & Who to Alert"
              subtitle="Competitive intelligence and notification preferences"
            />
            <div className="space-y-6">
              <Field label="Top 3–5 competitors to monitor">
                <div className="space-y-2">
                  {form.competitors.map((name, idx) => (
                    <input
                      key={`competitor-${idx}`}
                      type="text"
                      value={name}
                      onChange={(e) => updateCompetitor(idx, e.target.value)}
                      className={inputClass}
                      placeholder={`Competitor ${idx + 1}`}
                    />
                  ))}
                </div>
              </Field>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className={labelClass}>Dashboard users and their roles</p>
                  <button
                    type="button"
                    onClick={addUser}
                    className="inline-flex items-center gap-1 text-sm text-[#03396c] dark:text-blue-400 hover:underline"
                  >
                    <Plus className="h-4 w-4" />
                    Add user
                  </button>
                </div>
                <div className="space-y-4">
                  {form.dashboardUsers.map((user, idx) => (
                    <div
                      key={`user-${idx}`}
                      className="p-4 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 grid sm:grid-cols-2 gap-3 relative"
                    >
                      {form.dashboardUsers.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeUser(idx)}
                          className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500"
                          aria-label="Remove user"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <Field label="Name">
                        <input
                          type="text"
                          value={user.name}
                          onChange={(e) => updateUser(idx, 'name', e.target.value)}
                          className={inputClass}
                        />
                      </Field>
                      <Field label="Designation">
                        <input
                          type="text"
                          value={user.designation}
                          onChange={(e) => updateUser(idx, 'designation', e.target.value)}
                          className={inputClass}
                          placeholder="e.g. Head of Procurement"
                        />
                      </Field>
                      <Field label="Preferred alert channel" className="sm:col-span-2">
                        <select
                          value={user.alertChannel}
                          onChange={(e) => updateUser(idx, 'alertChannel', e.target.value)}
                          className={inputClass}
                        >
                          {ALERT_CHANNELS.map((ch) => (
                            <option key={ch} value={ch}>
                              {ch}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Weekly brief — preferred day">
                  <select
                    value={form.weeklyBriefDay}
                    onChange={(e) => update('weeklyBriefDay', e.target.value)}
                    className={inputClass}
                  >
                    {WEEKDAYS.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Weekly brief — preferred time">
                  <input
                    type="time"
                    value={form.weeklyBriefTime}
                    onChange={(e) => update('weeklyBriefTime', e.target.value)}
                    className={inputClass}
                  />
                </Field>
              </div>
            </div>
          </section>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="submit"
              className="flex-1 bg-[#03396c] hover:bg-[#022a4d] text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Save & open Command Centre
            </button>
            <button
              type="button"
              onClick={() => {
                setForm(initialCorporateFormState());
                setSubmitted(false);
                setError('');
              }}
              className="px-6 py-3 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 text-sm font-medium"
            >
              Reset form
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CorporatePage;
